<script setup lang="ts">
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { CreateCustomerRequest } from "@/models/requests/create-customer-request";
import { RouteName } from "@/router";
import { getCreateCustomerSchema } from "@/schemas/create-customer-schema";
import { customersService } from "@/services/customers-service";
import { notificationsService } from "@/services/notifications-service";
import { ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

function getDefaultFormData(): CreateCustomerRequest {
  return {
    name: "",
    customerNumber: "",
    phone: undefined,
    fax: undefined,
    homepage: undefined,
    organizationNumber: undefined,
    vat: undefined,
    relation: undefined,
    information: undefined,

    lockDate: undefined,
  } as CreateCustomerRequest;
}

const pageLoading = ref(false);
const loading = ref(false);
const formData = ref(getDefaultFormData());

const { result, validate } = useControlValidation(() =>
  getCreateCustomerSchema(),
);

async function onCreate() {
  loading.value = true;

  try {
    const valid = validate(formData.value);
    if (!valid) return;

    await customersService.createCustomer(formData.value);
    notificationsService.open({
      text: `Kund skapad.`,
      type: "success",
    });
    await router.push({ name: RouteName.Customers });
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid skapning av kund.",
      type: "error",
      error,
    });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">Skapa kund</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else class="columns">
      <div class="column is-6">
        <form @submit.prevent="onCreate">
          <div class="field">
            <label class="label" for="name">Namn</label>
            <control-validation
              :result="result"
              field="name"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="name"
                  v-model="formData.name"
                  class="input"
                  type="text"
                  name="name"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="customerNumber">Kundnummer</label>
            <control-validation
              :result="result"
              field="customerNumber"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="customerNumber"
                  v-model="formData.customerNumber"
                  class="input"
                  type="text"
                  name="customerNumber"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="phone">Telefonummer</label>
            <control-validation
              :result="result"
              field="phone"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="phone"
                  v-model="formData.phone"
                  class="input"
                  type="tel"
                  name="phone"
                  maxlength="20"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :class="{ 'is-loading': loading }"
            >
              Skapa
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
